import { useEffect, useState } from "react";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/ui/command";
import { Icone, ICONES } from "../visual.jsx";
import * as api from "../api.js";
import { useAcoes, useEstado } from "../estado.jsx";
import { Botao, Cortina, fecharComPergunta, Retrato } from "../componentes/comuns.jsx";

/**
 * Para quem encaminhar — a caixa que o WhatsApp Web abre ao encaminhar.
 *
 * Modal, e não menuzinho na bolha: escolher conversa exige buscar por nome numa
 * lista de duzentas, e isso não cabe ao lado de uma mensagem. Encaminha para uma
 * ou várias de uma vez, porque é como se usa — o print, o áudio e o documento
 * vão para o advogado juntos.
 *
 * A lista é o `Command` do shadcn: seta anda pelos nomes, Enter marca o que está
 * em foco e a caixa se anuncia como campo de busca com lista (`combobox`) — o
 * que a fila de `<button>` não fazia. O que ele NÃO faz aqui é filtrar:
 * `shouldFilter={false}` porque quem filtra é o banco (ver abaixo), e deixar o
 * cmdk peneirar de novo esconderia resultado que o FTS5 achou por radical.
 */
export default function Encaminhar() {
  const { encaminhando, mesa, caso } = useEstado();
  const { fecharEncaminhar, encaminharMensagens } = useAcoes();
  const [termo, setTermo] = useState("");
  const [escolhidos, setEscolhidos] = useState([]);
  /* O que o servidor devolveu, junto do termo que gerou — o par evita pintar o
     resultado de "ang" enquanto já se digitou "angelica". */
  const [busca, setBusca] = useState({ q: "", casos: [] });
  const daqui = caso?.registro?.id;
  const q = termo.trim();

  /* Escolher cinco conversas numa busca é trabalho: fechar sem mandar devolve a
     caixa vazia e a escolha começa do zero. */
  const sujo = escolhidos.length
    ? {
        titulo: `Sair sem encaminhar para ${escolhidos.length === 1 ? "a conversa escolhida" : `as ${escolhidos.length} conversas`}?`,
        texto: "A escolha se perde e nada é encaminhado.",
        acao: "Sair sem encaminhar",
      }
    : false;

  /*
   * Quem procura aqui procura no BANCO, não na página.
   *
   * `mesa` é a primeira página da lista — 200 dos 528 casos da planilha. Filtrar
   * ela era prometer uma busca que só achava quem já estava visível: o cliente
   * de número 300 não existia para o encaminhamento. `?q=` é a mesma busca de
   * FTS5 das outras telas (ver `src/leads.ts`).
   */
  useEffect(() => {
    if (!encaminhando || !q) return undefined;
    /* Espera a pessoa parar de digitar: consulta por tecla é o funil inteiro
       varrido a cada letra. */
    const relogio = setTimeout(async () => {
      const r = await api.mesaPagina({ q, limite: 40 }).catch(() => null);
      if (r) setBusca({ q, casos: r.corpo?.atendimentos ?? [] });
    }, 200);
    return () => clearTimeout(relogio);
  }, [q, encaminhando]);

  if (!encaminhando) return null;

  /* Sem termo, a caixa abre com o que a mesa já tem em mão: nada de pedido ao
     servidor para desenhar exatamente o que está na lista ao lado. */
  const procurando = Boolean(q) && busca.q !== q;
  const achados = (q ? (busca.q === q ? busca.casos : []) : (mesa ?? [])).filter((c) => c.id !== daqui).slice(0, 40);

  const alternar = (id) => setEscolhidos((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));
  const mandar = () => {
    /* Uma chamada por destino: a rota encaminha um lote de mensagens para UMA
       conversa, e o lote aqui é o inverso — uma mensagem para vários. */
    for (const id of escolhidos) encaminharMensagens(encaminhando.mensagens, id);
    setEscolhidos([]);
    fecharEncaminhar();
  };

  return (
    <Cortina
      aoFechar={fecharEncaminhar}
      sujo={sujo}
      largura="420px"
      /* `currentColor` + `text-primary`: a cor do ícone é token, e não o
         hexadecimal da terracota escrito à mão aqui. */
      antesDoTitulo={
        <span className="text-primary">
          <Icone de={ICONES.encaminhar} tamanho={18} />
        </span>
      }
      titulo="Encaminhar"
      acima={<span className="break-words">{encaminhando.resumo.slice(0, 120)}</span>}
      rodape={
        <>
          <Botao rotulo="Cancelar" aoTocar={fecharComPergunta(fecharEncaminhar, sujo)} />
          <Botao
            rotulo={`Encaminhar${escolhidos.length > 1 ? ` para ${escolhidos.length}` : ""}`}
            aoTocar={mandar}
            desabilitado={!escolhidos.length}
            destaque
          />
        </>
      }
    >
      <Command shouldFilter={false} className="bg-transparent p-0">
        <CommandInput
          value={termo}
          onValueChange={setTermo}
          autoFocus
          placeholder="Buscar conversa…"
          aria-label="Buscar conversa"
        />
        <CommandList className="mt-2.5 max-h-[320px]">
          <CommandEmpty className="py-2.5 text-left text-corpo text-muted-foreground">
            {procurando ? "Procurando…" : "Nenhuma conversa com esse nome."}
          </CommandEmpty>
          {achados.map((c) => {
            const marcado = escolhidos.includes(c.id);
            return (
              <CommandItem
                key={c.id}
                value={String(c.id)}
                onSelect={() => alternar(c.id)}
                /* `data-checked` é o que acende o ✓ do próprio `CommandItem` —
                   era o quadradinho verde desenhado à mão aqui dentro. */
                data-checked={marcado ? "true" : undefined}
                className="gap-2.5 rounded-[11px] px-2.5 py-2 data-[checked=true]:bg-verde-fundo/10 [&_svg]:text-verde"
              >
                <Retrato nome={c.nome} registro={c.id} tem={Boolean(c.tem_avatar)} tamanho={34} fonte={11} />
                <span className="min-w-0 flex-1">
                  <span className="block overflow-hidden text-corpo font-semibold text-ellipsis whitespace-nowrap">{c.nome}</span>
                  <span className="block text-selo font-normal text-muted-foreground">{c.etapa}</span>
                </span>
              </CommandItem>
            );
          })}
        </CommandList>
      </Command>
    </Cortina>
  );
}
